import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../auth/AuthProvider';
import { Link } from 'react-router-dom';
import { paths } from '../../config/paths';
import { hasSomePermission, permissions } from '../../config/permissions';
import { twMerge } from 'tailwind-merge';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon } from 'lucide-react';

const menuItems = [
  { path: paths.home, label: 'Time Tracking', permissions: [] },
  {
    path: paths.reports.workReport,
    label: 'Reports',
    permissions: [permissions.VIEW_REPORTS],
    children: [
      {
        path: paths.reports.workReport,
        label: 'Work Report',
        permissions: [permissions.VIEW_REPORTS],
      },
      {
        path: paths.reports.invoicingReport,
        label: 'Invoicing Report',
        permissions: [permissions.VIEW_REPORTS],
      },
    ],
  },
  {
    path: paths.roles,
    label: 'Roles',
    permissions: [permissions.MANAGE_USERS],
  },
];

export default function Menu() {
  return (
    <div className="flex items-center gap-4">
      {menuItems.map((item) => (
        <MenuItem
          level={1}
          key={item.path}
          path={item.path}
          label={item.label}
          permissions={item.permissions}
          children={item.children}
        />
      ))}
    </div>
  );
}

export const MenuItem = ({ path, label, permissions, children, level = 0 }) => {
  let timeout;
  const timeoutDuration = 400;

  const { user } = useAuthContext();
  const buttonRef = useRef(null);
  const [openState, setOpenState] = useState(false);

  const isActive =
    window.location.pathname === path ||
    (children &&
      children.some((child) => window.location.pathname === child.path));

  const hasPermission = useMemo(() => {
    if (permissions.length === 0) return true;
    return hasSomePermission(user?.permissions, permissions);
  }, [permissions, user?.permissions]);

  const toggleMenu = (open) => {
    setOpenState((openState) => !openState);
    buttonRef?.current?.click();
  };

  const onHover = (open, action) => {
    if (
      (!open && !openState && action === 'onMouseEnter') ||
      (open && openState && action === 'onMouseLeave')
    ) {
      clearTimeout(timeout);
      timeout = setTimeout(() => toggleMenu(open), timeoutDuration);
    }
  };

  const handleClick = (open) => {
    setOpenState(!open);
    clearTimeout(timeout);
  };

  const handleClickOutside = (event) => {
    if (buttonRef.current && !buttonRef.current.contains(event.target)) {
      event.stopPropagation();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  const LINK_STYLES = ['py-2 px-2', 'font-medium hover:text-blue-500 p-1'];

  if (!hasPermission) return null;

  if (children && children.length > 0) {
    return (
      <Popover className="relative">
        {({ open }) => (
          <div
            onMouseEnter={() => onHover(open, 'onMouseEnter')}
            onMouseLeave={() => onHover(open, 'onMouseLeave')}
            className="flex flex-col"
          >
            <PopoverButton
              className={twMerge(
                'font-medium hover:text-blue-500 outline-none focus:outline-none',
                isActive && 'text-blue-600',
              )}
              ref={buttonRef}
            >
              <div
                className={twMerge(
                  'flex justify-center gap-2 items-center',
                  ...LINK_STYLES,
                  isActive && 'text-blue-600',
                )}
                onClick={() => handleClick(open)}
              >
                {label}
                <ChevronDownIcon
                  className={twMerge(
                    open ? 'rotate-180' : '',
                    'h-4 w-4 inline-block',
                    'transition-all duration-500',
                  )}
                  aria-hidden="true"
                />
              </div>
            </PopoverButton>
            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                className={twMerge(
                  'absolute z-10 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5',
                  level > 1 ? 'ml-3 mt-4' : 'mt-0',
                )}
                anchor={level > 1 ? 'right start' : 'bottom start'}
              >
                <div className="p-3 flex flex-col gap-2">
                  {children.map((child) => (
                    <MenuItem
                      key={child.path}
                      path={child.path}
                      label={child.label}
                      permissions={child.permissions}
                      children={child.children || []}
                      level={level + 1}
                    />
                  ))}
                </div>
              </PopoverPanel>
            </Transition>
          </div>
        )}
      </Popover>
    );
  }

  return (
    <Link
      className={twMerge(...LINK_STYLES, isActive && 'text-blue-600')}
      to={path}
      style={{ textDecoration: 'none' }}
    >
      {label}
    </Link>
  );
};
